import React, { useState, useEffect, useRef, useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import { supabase } from '../lib/supabase'
import anime from 'animejs/lib/anime.es.js'
import useMagnetic from '../hooks/useMagnetic'

const prefersReducedMotion = () =>
  typeof window !== 'undefined' &&
  window.matchMedia &&
  window.matchMedia('(prefers-reduced-motion: reduce)').matches

const STRIPE_BASE = 'https://buy.stripe.com/4gM14ofaUgyM7zR4ILgnK00'

function buildStripeUrl(email, userId) {
  const params = new URLSearchParams()
  if (email) params.set('prefilled_email', email)
  if (userId) params.set('client_reference_id', userId)
  const qs = params.toString()
  return qs ? `${STRIPE_BASE}?${qs}` : STRIPE_BASE
}

export default function Landing({ session }) {
  const navigate = useNavigate()
  const [idea, setIdea] = useState('')
  const [handle, setHandle] = useState('')
  const [submitted, setSubmitted] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const [time, setTime] = useState('')
  const [memberCount, setMemberCount] = useState(null)
  const [feed, setFeed] = useState([])
  const [bootDone, setBootDone] = useState(false)
  const [member, setMember] = useState(null)
  // `memberCount` from member_count() filters to subscribed only, so it's the
  // wrong number for the NEXT MEMBER copy (would say #1 while 0 paid). Pull
  // the next sequence number from a separate RPC that counts all signups.
  // Falls back to null if the RPC doesn't exist yet — the copy then hides.
  const [nextMemberNumber, setNextMemberNumber] = useState(null)
  // Typewriter state: how many chars of each boot line are visible.
  // Final array element corresponds to the dynamic "members online" line.
  const [typedCharsPerLine, setTypedCharsPerLine] = useState([0, 0, 0, 0, 0])
  const [bootSequenceDone, setBootSequenceDone] = useState(false)
  const finalBootLineRef = useRef(null)
  // Number-roll: three live spans to swap in (boot final line, founders
  // callout, market data tile).
  const memberCountRefs = useRef([])
  const setCountRef = (idx) => (el) => { memberCountRefs.current[idx] = el }
  const prevMemberCountRef = useRef(null)
  // Wordmark glitch.
  const wordmarkRef = useRef(null)
  // Magnetic pull on the BUY IN button.
  const buyBtnRef = useMagnetic()

  // Boot line snapshots — final line's memberCount is frozen at boot time.
  // After typewriter completes, the live React render replaces the frozen
  // line and the number-roll effect drives the count.
  const bootLineSnapshots = useMemo(() => ([
    '> SYSTEM BOOT SEQUENCE INITIATED...',
    '> Mounting app library...................... [OK]',
    '> Loading membership database.............. [OK]',
    '> Connecting to exchange.................... [OK]',
    `> ${memberCount === null ? '—' : memberCount} members online · 5 apps live · MARKET OPEN`,
  ]), []) // Frozen on mount

  // Time tick + typewriter boot. Skips on return visits via localStorage.
  useEffect(() => {
    const t = setInterval(() => {
      setTime(new Date().toTimeString().slice(0, 8))
    }, 1000)
    setTime(new Date().toTimeString().slice(0, 8))

    let alreadyBooted = false
    try {
      alreadyBooted = typeof window !== 'undefined'
        && window.localStorage
        && window.localStorage.getItem('2ds_booted') === '1'
    } catch (e) { /* localStorage unavailable */ }

    if (alreadyBooted || prefersReducedMotion()) {
      setTypedCharsPerLine(bootLineSnapshots.map(l => l.length))
      setBootSequenceDone(true)
      setBootDone(true)
      try { if (window.localStorage) window.localStorage.setItem('2ds_booted', '1') } catch (e) {}
      return () => clearInterval(t)
    }

    let cancelled = false
    let activeLine = 0
    let charsInLine = 0
    const timers = []

    const advance = () => {
      if (cancelled) return
      if (activeLine >= bootLineSnapshots.length) {
        setBootSequenceDone(true)
        setBootDone(true)
        try { if (window.localStorage) window.localStorage.setItem('2ds_booted', '1') } catch (e) {}
        // Pulse the final line once after fully typing.
        if (finalBootLineRef.current) {
          anime({
            targets: finalBootLineRef.current,
            opacity: [1, 0.55, 1],
            scale: [1, 1.04, 1],
            duration: 600,
            easing: 'easeOutQuad',
          })
        }
        return
      }
      charsInLine += 1
      setTypedCharsPerLine(prev => {
        const next = prev.slice()
        next[activeLine] = charsInLine
        return next
      })
      if (charsInLine < bootLineSnapshots[activeLine].length) {
        timers.push(setTimeout(advance, 25))
      } else {
        activeLine += 1
        charsInLine = 0
        timers.push(setTimeout(advance, 200))
      }
    }
    timers.push(setTimeout(advance, 100))

    return () => {
      cancelled = true
      clearInterval(t)
      timers.forEach(clearTimeout)
    }
  }, [bootLineSnapshots])

  // Public read path: pulls real member_count + last-5 joins via SECURITY DEFINER
  // RPCs. No emails exposed. Refetches every 15s.
  useEffect(() => {
    let cancelled = false
    const fetchPublic = async () => {
      const { data: cnt } = await supabase.rpc('member_count')
      if (!cancelled && typeof cnt === 'number') setMemberCount(cnt)
      const { data: recent } = await supabase.rpc('recent_members', { limit_n: 5 })
      if (!cancelled && Array.isArray(recent)) {
        setFeed(recent.map(m => ({
          id: m.member_number,
          memberNumber: m.member_number,
          displayName: m.display_name,
          time: new Date(m.created_at).toTimeString().slice(0, 8)
        })))
      }
      // next_member_number is from a separate RPC (sql/next_member_number.sql).
      // Tolerates the RPC not yet existing — leaves nextMemberNumber null and
      // the NEXT MEMBER copy hides until it's deployed.
      const { data: nextNum, error: nextErr } = await supabase.rpc('next_member_number')
      if (!cancelled) {
        if (!nextErr && typeof nextNum === 'number') {
          setNextMemberNumber(nextNum)
        } else if (nextErr) {
          // Don't spam the console; just one warning.
          console.warn('next_member_number RPC unavailable:', nextErr.message)
        }
      }
    }
    fetchPublic()
    const interval = setInterval(fetchPublic, 15000)
    return () => {
      cancelled = true
      clearInterval(interval)
    }
  }, [])

  // When signed in, look up the member row so the BUY IN button can decide
  // whether to send them to Stripe (unpaid) or the dashboard (paid).
  useEffect(() => {
    if (!session?.user) { setMember(null); return }
    let cancelled = false
    supabase.from('members')
      .select('is_subscribed')
      .eq('user_id', session.user.id)
      .maybeSingle()
      .then(({ data }) => {
        if (!cancelled) setMember(data || null)
      })
    return () => { cancelled = true }
  }, [session])

  // Number-roll on memberCount change. Animates all three live spans together
  // by interpolating a scratch object and writing textContent each frame.
  useEffect(() => {
    if (memberCount === null) return
    const refs = memberCountRefs.current.filter(Boolean)
    if (refs.length === 0) return
    const target = String(memberCount)
    if (prefersReducedMotion()) {
      refs.forEach(r => { r.textContent = target })
      prevMemberCountRef.current = memberCount
      return
    }
    const from = prevMemberCountRef.current == null ? 0 : prevMemberCountRef.current
    const obj = { v: from }
    refs.forEach(r => { r.textContent = String(from) })
    anime({
      targets: obj,
      v: memberCount,
      duration: 600,
      easing: 'easeOutExpo',
      round: 1,
      update: () => {
        const s = String(obj.v)
        refs.forEach(r => { if (r) r.textContent = s })
      },
    })
    prevMemberCountRef.current = memberCount
  }, [memberCount])

  // Random glitch flash on the wordmark every 20-60s.
  useEffect(() => {
    if (prefersReducedMotion()) return undefined
    let cancelled = false
    const timers = []
    const schedule = () => {
      if (cancelled) return
      const delay = 20000 + Math.random() * 40000
      timers.push(setTimeout(() => {
        if (cancelled || !wordmarkRef.current) return
        anime({
          targets: wordmarkRef.current,
          keyframes: [
            { translateX: -3, translateY: 2 },
            { translateX: 3, translateY: -2 },
            { translateX: -2, translateY: 1 },
            { translateX: 0, translateY: 0 },
          ],
          duration: 220,
          easing: 'steps(2)',
        })
        schedule()
      }, delay))
    }
    schedule()
    return () => {
      cancelled = true
      timers.forEach(clearTimeout)
      if (wordmarkRef.current) anime.remove(wordmarkRef.current)
    }
  }, [])

  const handleBuyIn = () => {
    if (!session?.user) {
      navigate('/signup')
      return
    }
    if (member?.is_subscribed) {
      navigate('/dashboard')
      return
    }
    window.location.href = buildStripeUrl(session.user.email, session.user.id)
  }

  const handleIdeaSubmit = async () => {
    if (!idea.trim()) return
    setSubmitting(true)
    await supabase.from('ideas').insert([{ idea, tiktok_handle: handle }])
    setSubmitted(true)
    setSubmitting(false)
  }

  const tickerItems = [
    ['2DS', '$2.00', '▲ ALL TIME'],
    ['RIDDLE', 'LIVE', '● ACTIVE'],
    ['ROAST', 'LIVE', '● ACTIVE'],
    ['DARK_HISTORY', 'LIVE', '● ACTIVE'],
    ['MEMBERS', memberCount === null ? '—' : memberCount.toString(), '▲ GROWING'],
    ['NEXT_DROP', 'WEEKLY', '● GUARANTEED'],
  ]

  const s = {
    body: { background: '#000', color: '#ff0055', fontFamily: "'Share Tech Mono', monospace", minHeight: '100vh', overflowX: 'hidden' },
    wrap: { maxWidth: 520, margin: '0 auto', padding: '0 0 100px', position: 'relative', zIndex: 2 },
    tickerStrip: { background: 'rgba(255,0,80,0.06)', borderBottom: '1px solid rgba(255,0,80,0.15)', overflow: 'hidden', position: 'relative', display: 'flex', alignItems: 'center' },
    tickerLabel: { background: '#ff0055', color: '#000', fontSize: 10, fontWeight: 700, letterSpacing: 1, padding: '10px 12px', flexShrink: 0, zIndex: 2 },
    tickerOuter: { overflow: 'hidden', flex: 1, maskImage: 'linear-gradient(90deg, transparent, black 5%, black 95%, transparent)' },
    tickerInner: { display: 'flex', animation: 'tick 25s linear infinite', whiteSpace: 'nowrap' },
    ti: { display: 'flex', alignItems: 'center', gap: 8, padding: '10px 20px', borderRight: '1px solid rgba(255,0,80,0.08)', fontSize: 10, letterSpacing: 1 },
    exHeader: { padding: '16px 20px', borderBottom: '1px solid rgba(255,0,80,0.08)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' },
    exName: { fontSize: 11, letterSpacing: 3, color: 'rgba(255,0,80,0.8)' },
    exRight: { display: 'flex', alignItems: 'center', gap: 12 },
    marketOpen: { display: 'flex', alignItems: 'center', gap: 5, fontSize: 9, color: '#ff0055', letterSpacing: 1 },
    moDot: { width: 5, height: 5, borderRadius: '50%', background: '#ff0055', animation: 'pulse 1.5s ease infinite' },
    exTime: { fontSize: 11, color: '#ff0055', letterSpacing: 1 },
    bootZone: { padding: 20, borderBottom: '1px solid rgba(255,0,80,0.06)' },
    bootLine: (delay, bright) => ({ fontSize: 10, color: bright ? '#ff0055' : 'rgba(255,0,80,0.7)', marginBottom: 2, opacity: 0, animation: `bootIn 0.3s ease ${delay}s forwards` }),
    heroZone: { borderBottom: '1px solid rgba(255,0,80,0.08)', position: 'relative', overflow: 'hidden' },
    ghostPrice: { position: 'absolute', fontFamily: "'Bebas Neue', sans-serif", fontSize: 320, color: 'rgba(255,0,80,0.04)', letterSpacing: -12, lineHeight: 1, top: -20, right: -20, pointerEvents: 'none', userSelect: 'none', zIndex: 0 },
    heroTop: { padding: '28px 20px 0', position: 'relative', zIndex: 1, opacity: bootDone ? 1 : 0, transform: bootDone ? 'translateY(0)' : 'translateY(8px)', transition: 'all 0.5s ease' },
    wordmark: { display: 'flex', alignItems: 'flex-end', gap: 0, marginBottom: 4 },
    wmTwo: { fontFamily: "'Bebas Neue', sans-serif", fontSize: 96, color: '#ff0055', letterSpacing: -3, lineHeight: 0.85, textShadow: '0 0 40px rgba(255,0,80,0.4)' },
    wmDollar: { fontFamily: "'Bebas Neue', sans-serif", fontSize: 48, color: 'rgba(255,0,80,0.7)', letterSpacing: -2, lineHeight: 0.85, marginBottom: 6 },
    wmSites: { fontFamily: "'Share Tech Mono', monospace", fontSize: 13, letterSpacing: 6, color: 'rgba(255,0,80,0.8)', marginLeft: 8, marginBottom: 12, textTransform: 'uppercase' },
    heroRule: { height: 1, background: 'linear-gradient(90deg, #ff0055, rgba(255,0,80,0.08))', marginBottom: 10 },
    heroMeta: { display: 'flex', justifyContent: 'space-between', fontSize: 9, letterSpacing: 2, color: 'rgba(255,0,80,0.65)', paddingBottom: 24 },
    heroQuote: { padding: '0 20px 24px', position: 'relative', zIndex: 1 },
    hqLabel: { fontSize: 9, letterSpacing: 3, color: 'rgba(255,0,80,0.75)', marginBottom: 6 },
    hqPrice: { fontFamily: "'Bebas Neue', sans-serif", fontSize: 110, color: '#ff0055', letterSpacing: -5, lineHeight: 0.88, textShadow: '0 0 50px rgba(255,0,80,0.3)' },
    hqMo: { fontFamily: "'Bebas Neue', sans-serif", fontSize: 28, color: 'rgba(255,0,80,0.6)', letterSpacing: -1 },
    hqArrow: { fontSize: 10, color: '#ff0055', letterSpacing: 1, margin: '8px 0 12px', display: 'flex', alignItems: 'center', gap: 8 },
    hqDesc: { fontSize: 13, color: 'rgba(255,0,80,0.85)', lineHeight: 1.8, marginBottom: 24, paddingLeft: 14, borderLeft: '2px solid rgba(255,0,80,0.5)' },
    buyBtn: { width: '100%', background: '#ff0055', color: '#000', border: 'none', padding: '20px 16px', fontFamily: "'Share Tech Mono', monospace", fontSize: 15, letterSpacing: 2, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 10, transition: 'all 0.2s', fontWeight: 700, boxShadow: '0 0 40px rgba(255,0,80,0.3)' },
    marketData: { display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', borderBottom: '1px solid rgba(255,0,80,0.08)' },
    md: (last) => ({ padding: '14px 20px', borderRight: last ? 'none' : '1px solid rgba(255,0,80,0.06)', textAlign: 'center' }),
    mdLabel: { fontSize: 8, letterSpacing: 2, color: 'rgba(255,0,80,0.65)', marginBottom: 4 },
    mdVal: { fontFamily: "'Bebas Neue', sans-serif", fontSize: 36, color: '#ff0055', letterSpacing: -1, lineHeight: 1, textShadow: '0 0 20px rgba(255,0,80,0.3)' },
    mdSub: { fontSize: 8, color: 'rgba(255,0,80,0.55)', letterSpacing: 1, marginTop: 2 },
    holdingsSection: { padding: '16px 20px', borderBottom: '1px solid rgba(255,0,80,0.08)' },
    sectionHdr: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 },
    shTitle: { fontSize: 9, letterSpacing: 2, color: 'rgba(255,0,80,0.75)' },
    shMeta: { fontSize: 9, color: 'rgba(255,0,80,0.6)', letterSpacing: 1 },
    holding: { display: 'grid', gridTemplateColumns: '28px 1fr auto', gap: 10, alignItems: 'center', padding: '12px 0', borderBottom: '1px solid rgba(255,0,80,0.05)' },
    hIdx: { fontFamily: "'Bebas Neue', sans-serif", fontSize: 22, color: 'rgba(255,0,80,0.2)', lineHeight: 1 },
    hSym: { fontSize: 14, fontWeight: 800, color: '#ff0055', fontFamily: "'Space Grotesk', sans-serif", marginBottom: 2 },
    hDesc: { fontSize: 11, color: 'rgba(255,0,80,0.65)', lineHeight: 1.4 },
    hRight: { textAlign: 'right', display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 4 },
    hLive: { fontSize: 8, color: '#ff0055', letterSpacing: 1, display: 'flex', alignItems: 'center', gap: 3 },
    hLiveDot: { width: 4, height: 4, borderRadius: '50%', background: '#ff0055', animation: 'pulse 1.5s ease infinite' },
    hSoon: { fontSize: 8, color: 'rgba(255,0,80,0.45)', letterSpacing: 1 },
    hEmoji: { fontSize: 18, opacity: 0.7 },
    orderBook: { padding: '16px 20px', borderBottom: '1px solid rgba(255,0,80,0.08)' },
    obHdr: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 },
    obTitle: { fontSize: 9, letterSpacing: 2, color: 'rgba(255,0,80,0.75)' },
    obLive: { display: 'flex', alignItems: 'center', gap: 5, fontSize: 9, color: '#ff0055', letterSpacing: 1 },
    obRow: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 0', borderBottom: '1px solid rgba(255,0,80,0.04)', fontSize: 10 },
    obAction: { color: 'rgba(255,0,80,0.85)' },
    obTime: { fontSize: 9, color: 'rgba(255,0,80,0.55)' },
    ideaSection: { padding: 20 },
    isHdr: { fontSize: 9, letterSpacing: 2, color: 'rgba(255,0,80,0.75)', marginBottom: 4 },
    isSub: { fontSize: 10, color: 'rgba(255,0,80,0.7)', marginBottom: 16, lineHeight: 1.6 },
    textarea: { width: '100%', background: 'rgba(255,0,80,0.03)', border: '1px solid rgba(255,0,80,0.2)', borderRadius: 0, padding: 12, fontSize: 11, color: '#ff0055', fontFamily: "'Share Tech Mono', monospace", outline: 'none', resize: 'none', marginBottom: 10, lineHeight: 1.6 },
    input: { background: 'rgba(255,0,80,0.03)', border: '1px solid rgba(255,0,80,0.2)', borderRadius: 0, padding: '11px 12px', fontSize: 11, color: '#ff0055', fontFamily: "'Share Tech Mono', monospace", outline: 'none', flex: 1 },
    irBtn: { background: 'none', border: '1px solid rgba(255,0,80,0.5)', color: '#ff0055', fontFamily: "'Share Tech Mono', monospace", fontSize: 10, padding: '11px 16px', cursor: 'pointer', letterSpacing: 1 },
    footer: { padding: '12px 20px', borderTop: '1px solid rgba(255,0,80,0.08)', display: 'flex', justifyContent: 'space-between', fontSize: 9, color: 'rgba(255,0,80,0.5)', letterSpacing: 1.5 },
    foundersCallout: {
      borderTop: '1px solid rgba(255,0,80,0.5)',
      background: 'rgba(255,0,80,0.08)',
      padding: '14px 16px',
      fontFamily: "'Share Tech Mono', monospace",
      fontSize: 11,
      letterSpacing: 1.5,
      color: '#ff0055',
      textTransform: 'uppercase',
      textAlign: 'center',
      boxShadow: '0 0 24px rgba(255,0,80,0.18) inset',
      marginTop: 12,
    },
    foundersDot: { display: 'inline-block', width: 6, height: 6, borderRadius: '50%', background: '#ff0055', marginRight: 8, verticalAlign: 'middle', animation: 'pulse 1.5s ease infinite' },
  }

  const holdings = [
    { idx: '01', sym: 'DAILY_RIDDLE', desc: 'One riddle per day. Global leaderboard. Fastest time wins.', emoji: '🧩', status: 'live', route: '/app/riddle' },
    { idx: '02', sym: 'AI_ROAST_ME', desc: 'Enter your name. Get destroyed by AI. Share it everywhere.', emoji: '🔥', status: 'live', route: '/app/roast' },
    { idx: '03', sym: 'DARK_HISTORY', desc: 'Enter birthday. Get dark historical events. Unsettling guaranteed.', emoji: '👻', status: 'live', route: '/app/dark-history' },
    { idx: '04', sym: 'VILLAIN_ORIGIN', desc: 'Forge your villain identity. Custom origin story. Screenshot the dossier.', emoji: '🦹', status: 'live', route: '/app/villain' },
    { idx: '05', sym: 'ROGUES_GALLERY', desc: 'Every villain forged by every member. The vault.', emoji: '🗂️', status: 'live', route: '/app/rogues' },
  ]

  return (
    <div style={s.body}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Share+Tech+Mono&family=Bebas+Neue&family=Space+Grotesk:wght@400;700;800&display=swap');
        @keyframes tick { 0% { transform: translateX(0) } 100% { transform: translateX(-50%) } }
        @keyframes pulse { 0%,100% { opacity:1; transform:scale(1) } 50% { opacity:0.3; transform:scale(1.4) } }
        @keyframes bootIn { from { opacity:0; transform:translateX(-8px) } to { opacity:1; transform:translateX(0) } }
        @keyframes shimmer { 0% { left:-100% } 100% { left:200% } }
        @keyframes glitch { 0%,94%,100% { transform:translate(0) } 95% { transform:translate(-3px,1px) } 97% { transform:translate(3px,-1px) } }
        @keyframes rowIn { from { opacity:0; transform:translateY(-6px) } to { opacity:1; transform:translateY(0) } }
        @keyframes blink { 0%,100% { opacity:1 } 50% { opacity:0 } }
        .buy-btn:hover { background: #fff !important; box-shadow: 0 0 40px rgba(255,0,80,0.5) !important; }
        .holding-row:hover { background: rgba(255,0,80,0.03); }
        .ir-btn:hover { background: #ff0055 !important; color: #000 !important; }
        body { cursor: none; }
        * { box-sizing: border-box; margin: 0; padding: 0; }
        .scanlines { position: fixed; inset: 0; background: repeating-linear-gradient(0deg, transparent, transparent 2px, rgba(0,0,0,0.08) 2px, rgba(0,0,0,0.08) 3px); pointer-events: none; z-index: 100; }
        .grid-bg { position: fixed; inset: 0; background-image: linear-gradient(rgba(255,0,80,0.025) 1px, transparent 1px), linear-gradient(90deg, rgba(255,0,80,0.025) 1px, transparent 1px); background-size: 28px 28px; pointer-events: none; z-index: 0; }
        .vignette { position: fixed; inset: 0; background: radial-gradient(ellipse at center, transparent 30%, rgba(0,0,0,0.9) 100%); pointer-events: none; z-index: 0; }
        .glow1 { position: fixed; width: 600px; height: 600px; border-radius: 50%; background: radial-gradient(circle, rgba(255,0,80,0.06) 0%, transparent 70%); top: -200px; left: -200px; pointer-events: none; z-index: 0; animation: drift1 12s ease-in-out infinite; }
        .glow2 { position: fixed; width: 500px; height: 500px; border-radius: 50%; background: radial-gradient(circle, rgba(144,0,224,0.05) 0%, transparent 70%); bottom: -150px; right: -150px; pointer-events: none; z-index: 0; animation: drift2 15s ease-in-out infinite; }
        @keyframes drift1 { 0%,100% { transform: translate(0,0) } 50% { transform: translate(40px,40px) } }
        @keyframes drift2 { 0%,100% { transform: translate(0,0) } 50% { transform: translate(-40px,-30px) } }
      `}</style>

      <div className="scanlines" />
      <div className="grid-bg" />
      <div className="vignette" />
      <div className="glow1" />
      <div className="glow2" />

      <div style={s.wrap}>
        {/* TICKER */}
        <div style={s.tickerStrip}>
          <div style={s.tickerLabel}>2DS</div>
          <div style={s.tickerOuter}>
            <div style={s.tickerInner}>
              {[...tickerItems, ...tickerItems].map(([sym, val, up], i) => (
                <div key={i} style={s.ti}>
                  <span style={{ color: '#ff0055', fontWeight: 700 }}>{sym}</span>
                  <span style={{ color: 'rgba(255,0,80,0.5)' }}>{val}</span>
                  <span style={{ color: '#ff0055' }}>{up}</span>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* EXCHANGE HEADER */}
        <div style={s.exHeader}>
          <div style={s.exName}>2DOLLARSITES EXCHANGE</div>
          <div style={s.exRight}>
            <div style={s.marketOpen}><div style={s.moDot} /><span>MARKET OPEN</span></div>
            <div style={s.exTime}>{time}</div>
          </div>
        </div>

        {/* BOOT SEQUENCE — typewriter, char-by-char */}
        <div style={s.bootZone}>
          {bootLineSnapshots.map((line, i) => {
            const isFinal = i === bootLineSnapshots.length - 1
            const bright = i === 0 || isFinal
            const typed = typedCharsPerLine[i] || 0
            const baseStyle = {
              fontSize: 10,
              color: bright ? '#ff0055' : 'rgba(255,0,80,0.7)',
              marginBottom: 2,
              minHeight: '1em',
            }
            if (isFinal && bootSequenceDone) {
              return (
                <div key={i} ref={finalBootLineRef} style={baseStyle}>
                  &gt; <span ref={setCountRef(0)}>{memberCount === null ? '—' : memberCount}</span> members online · 5 apps live · MARKET OPEN
                </div>
              )
            }
            const visible = line.slice(0, typed)
            const typing = typed > 0 && typed < line.length
            return (
              <div key={i} ref={isFinal ? finalBootLineRef : null} style={baseStyle}>
                {visible}{typing && <span style={{ animation: 'blink 1s infinite' }}>_</span>}
              </div>
            )
          })}
        </div>

        {/* HERO */}
        <div style={s.heroZone}>
          <div style={s.ghostPrice} aria-hidden="true">$2</div>
          <div style={s.heroTop}>
            <div ref={wordmarkRef} style={s.wordmark}>
              <span style={s.wmTwo}>2</span>
              <span style={s.wmDollar}>$</span>
              <span style={s.wmSites}>SITES</span>
            </div>
            <div style={s.heroRule} />
            <div style={s.heroMeta}>
              <span>MEMBERS ONLY</span>
              <span>EST. 2026</span>
              <span>NEW DROP WEEKLY</span>
            </div>
          </div>
          <div style={s.heroQuote}>
            <div style={s.hqLabel}>CURRENT PRICE · MEMBERSHIP ACCESS</div>
            <div style={{ ...s.hqPrice, animation: 'glitch 8s ease infinite' }}>
              $2<span style={s.hqMo}>/mo</span>
            </div>
            <div style={s.hqArrow}>
              <span style={{ width: 16, height: 1, background: '#ff0055', display: 'inline-block' }} />
              ▲ NEVER GOING UP · CANCEL ANYTIME
            </div>
            <div style={s.hqDesc}>
              A growing library of mini apps, games, and experiences. Members only. New drop every week. The library never stops growing.
            </div>
            <button
              ref={buyBtnRef}
              className="buy-btn"
              style={s.buyBtn}
              onClick={handleBuyIn}
            >
              ▶ JOIN FOR $2 — FORGE YOUR MEMBERSHIP →
            </button>
            {(memberCount === null || memberCount < 100) && (
              <div style={s.foundersCallout}>
                <span style={s.foundersDot} />FOUNDERS PRICING ACTIVE · First 100 members locked at $2/mo FOREVER · <span ref={setCountRef(1)}>{memberCount === null ? '—' : memberCount}</span>/100 claimed
              </div>
            )}
            {nextMemberNumber !== null && (
              <div style={{ fontSize: 9, color: 'rgba(255,0,80,0.6)', textAlign: 'center', marginTop: 10, letterSpacing: 1 }}>
                NEXT MEMBER: #{nextMemberNumber} · JOIN BEFORE SOMEONE ELSE DOES
              </div>
            )}
          </div>
        </div>

        {/* MARKET DATA */}
        <div style={s.marketData}>
          {[
            { label: 'APPS LIVE', val: '5+', sub: 'GROWING WEEKLY' },
            { label: 'PRICE', val: '$2', sub: 'NEVER GOING UP' },
            { label: 'MEMBERS', val: memberCount === null ? '—' : memberCount, sub: 'ONLINE NOW', countRef: setCountRef(2) },
          ].map((tile, i) => (
            <div key={i} style={s.md(i === 2)}>
              <div style={s.mdLabel}>{tile.label}</div>
              <div style={s.mdVal}>
                {tile.countRef ? <span ref={tile.countRef}>{tile.val}</span> : tile.val}
              </div>
              <div style={s.mdSub}>{tile.sub}</div>
            </div>
          ))}
        </div>

        {/* HOLDINGS */}
        <div style={s.holdingsSection}>
          <div style={s.sectionHdr}>
            <div style={s.shTitle}>// PORTFOLIO · APP LIBRARY</div>
            <div style={s.shMeta}>5 ACTIVE</div>
          </div>
          {holdings.map((h, i) => (
            <div
              key={i}
              className="holding-row"
              style={{ ...s.holding, opacity: h.status === 'soon' ? 0.3 : 1, cursor: h.route ? 'pointer' : 'default', borderBottom: i < holdings.length - 1 ? '1px solid rgba(255,0,80,0.05)' : 'none' }}
              onClick={() => h.route && navigate(h.route)}
            >
              <div style={s.hIdx}>{h.idx}</div>
              <div>
                <div style={s.hSym}>{h.sym}</div>
                <div style={s.hDesc}>{h.desc}</div>
              </div>
              <div style={s.hRight}>
                {h.status === 'live'
                  ? <div style={s.hLive}><div style={s.hLiveDot} />LIVE</div>
                  : <div style={s.hSoon}>○ PENDING</div>
                }
                <span style={s.hEmoji}>{h.emoji}</span>
              </div>
            </div>
          ))}
        </div>

        {/* ORDER BOOK */}
        <div style={s.orderBook}>
          <div style={s.obHdr}>
            <div style={s.obTitle}>// ORDER BOOK · LIVE ACTIVITY</div>
            <div style={s.obLive}><div style={s.moDot} /><span>LIVE</span></div>
          </div>
          {feed.length === 0 ? (
            <div style={{ ...s.obRow, borderBottom: 'none', color: 'rgba(255,0,80,0.45)', justifyContent: 'center', padding: '14px 0' }}>
              <span>// AWAITING_FIRST_JOIN — BE MEMBER #1</span>
            </div>
          ) : feed.map((item, i) => (
            <div key={item.id} style={{ ...s.obRow, borderBottom: i < feed.length - 1 ? '1px solid rgba(255,0,80,0.04)' : 'none', animation: 'rowIn 0.3s ease' }}>
              <span style={s.obAction}>
                Member <b>#{item.memberNumber}</b> joined as <b>{item.displayName}</b>
              </span>
              <span style={s.obTime}>{item.time}</span>
            </div>
          ))}
        </div>

        {/* IDEA SUBMISSION */}
        <div style={s.ideaSection}>
          <div style={s.isHdr}>// SUBMIT_ORDER — COMMUNITY IDEAS</div>
          <div style={s.isSub}>If we build it, your TikTok handle gets credited on the page. Forever.</div>
          {submitted ? (
            <div style={{ color: '#ff0055', fontSize: 12, letterSpacing: 1, padding: '16px 0' }}>▶ ORDER PLACED — WE'LL REVIEW IT</div>
          ) : (
            <>
              <textarea
                rows={3}
                placeholder="// describe your idea for a mini site or game..."
                style={s.textarea}
                value={idea}
                onChange={e => setIdea(e.target.value)}
              />
              <div style={{ display: 'flex', gap: 8 }}>
                <input
                  placeholder="// your @tiktok handle"
                  style={s.input}
                  value={handle}
                  onChange={e => setHandle(e.target.value)}
                />
                <button className="ir-btn" style={s.irBtn} onClick={handleIdeaSubmit} disabled={submitting}>
                  {submitting ? '...' : 'PLACE ORDER'}
                </button>
              </div>
            </>
          )}
        </div>

        <div style={s.footer}>
          <span>2DOLLARSITES.COM</span>
          <span>MARKET OPEN 24/7</span>
          <span>BUILT AT 2AM · LA · EST.2026</span>
        </div>
      </div>
    </div>
  )
}
